// ChatHomeComposer — composer CENTRAL da nova superfície Chat (fatia A1/A2).
//
// A0+A1: textarea + anexar QUALQUER arquivo (drop universal) + seletor de modelo
// PLUGADO NO ADAPTERS + linha de modos (Chat ligado; Imagem/Vídeo/Plan/Deep
// research presentes mas adiados pra A3) + atalhos rápidos. Reusa os mesmos
// hooks do composer do Code (useComposerModels → Adapters, useFileAttachments →
// drop), sem nenhum chrome de github. Estilo via tokens warm do .cx-shell.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowUp,
  ArrowUpRight,
  ChevronDown,
  SlidersHorizontal,
  Sparkles,
  ShieldQuestion,
  Bug,
  ClipboardList,
  Image as ImageIcon,
  MessageCircle,
  Loader2,
  MessageCircleQuestion,
  Mic,
  Paperclip,
  Telescope,
  Video,
  X,
} from 'lucide-react';
import { useAgentStore } from '../../../store/useAgentStore.js';
import { useComposerModels } from '../../../hooks/useComposerModels.js';
import { useFileAttachments } from '../../../hooks/useFileAttachments.js';
import WorkbenchPopover from '../../workbench/WorkbenchPopover.jsx';
import ModelDropdown from '../../code/ModelDropdown.jsx';
import SlashCommandAutocomplete from '../../code/SlashCommandAutocomplete.jsx';
import { insertSlashToken } from '../../../hooks/useSlashCommands.js';
import ChatContextPicker from './ChatContextPicker.jsx';
import { contextToSessionInput } from '../../../lib/chatContext.js';
import { detectAtMenuPrefix } from '../../../lib/atMenu.js';
import { clearDraft, readDraft, writeDraft, listDrafts } from '../../../lib/composer/draftStore.js';
import { loadAttachmentDraft, saveAttachmentDraft } from '../../../lib/composer/attachmentDrafts.js';
import ComposerSubmitError from '../../code/ComposerSubmitError.jsx';
import { createClientIntentId } from '../../../lib/composer/newChatIntent.js';

// Modos do composer. Só 'chat' liga em A1; os demais aparecem (descoberta) mas
// são adiados pra A3 (Imagem = subsistema de geração com seleção de modelo real;
// Vídeo = a definir; Plan/Deep research = comportamento do turn).
const MODES = [
  { id: 'chat', label: 'Chat', icon: MessageCircle, ready: true },
  { id: 'auto', label: 'Auto', icon: Sparkles, ready: true },
  { id: 'plan', label: 'Plan', icon: ClipboardList, ready: true },
  { id: 'research', label: 'Deep research', icon: Telescope, ready: true },
  // Recuperados do Aba-Apolo — transforms reais via ChatWorkspace MODE_PREFIX.
  { id: 'debug', label: 'Debug', icon: Bug, ready: true },
  { id: 'ask', label: 'Ask', icon: MessageCircleQuestion, ready: true },
  { id: 'image', label: 'Imagem', icon: ImageIcon, ready: true },
  { id: 'video', label: 'Vídeo', icon: Video, ready: true },
];

// Modos de CRIAÇÃO (imagem/vídeo). O SELETOR de modelo é SEMPRE o sistema real de
// adapters (⌘E) — não há mais catálogo hardcoded desconectado. A geração liga
// quando houver um provider de imagem/vídeo configurado nos Adapters; até lá o
// modo só ajusta o placeholder e avisa (seam honesto). Os providers ficam citados
// na mensagem pra descoberta.
const CREATION_INFO = {
  image: { noun: 'a imagem', label: 'Imagem', providers: 'DALL·E, Ideogram, Leonardo, Stability' },
  video: { noun: 'o vídeo', label: 'Vídeo', providers: 'Sora, Runway, Veo, Kling, Pika' },
};

const QUICK = [
  { label: 'Código', text: 'Me ajude com código: ' },
  { label: 'Criar', text: 'Quero criar ' },
  { label: 'Estratégias', text: 'Pense numa estratégia para ' },
  { label: 'Escrever', text: 'Escreva ' },
  { label: 'Aprender', text: 'Me ensine sobre ' },
];

import { useAuthStore } from '../../../store/useAuthStore.js';
import { projectEnvironment, saveProjectEnvironment } from '../../../lib/code/projectRegistry.js';

export default function ChatHomeComposer({ onSubmit, newChatRequest, onNewChatHandled, unified = false }) {
  const account = useAuthStore((s) => s.user?.id || 'anonymous');
  const profile = useAuthStore((s) => s.activeProfileId || 'default');
  const draftScope = `${account}:${profile}:chat`;
  const projectScope = `${account}:${profile}`;
  const [initialDraft] = useState(() => readDraft(typeof window !== 'undefined' ? window.localStorage : null, draftScope));
  const environmentChosen = useRef(Boolean(initialDraft?.environment));
  const [text, setText] = useState(() => initialDraft?.text || '');
  const [clientIntentId, setClientIntentId] = useState(() => initialDraft?.client_intent_id || createClientIntentId());
  const [environment, setEnvironment] = useState(() => initialDraft?.environment || 'direct');
  const [permissionsMode, setPermissionsMode] = useState(() => initialDraft?.permissions_mode || 'ask');
  const [mode, setMode] = useState(() => initialDraft?.mode || 'chat');
  // Contexto anexado à sessão de chat (o que o `@` / pill "Contexto" escolhe):
  //   null | { kind:'folder', path, isGit } | { kind:'github', repo }
  // Sem contexto → conversa pura. Com pasta/repo → vira o directory da sessão e
  // as tools de arquivo operam (senão "o chat não edita nada"). A resolução do
  // repo (clone/checkout local) acontece dentro do createCodeSession.
  const [context, setContext] = useState(() => initialDraft?.github_repo ? { kind: 'github', repo: initialDraft.github_repo } : initialDraft?.directory ? { kind: 'folder', path: initialDraft.directory } : null);
  useEffect(() => {
    if (!unified) return;
    useAgentStore.setState((state) => ({ code: { ...state.code, newChatContext: { scope: projectScope, directory: context?.path || null, github_repo: context?.repo || null } } }));
  }, [unified, projectScope, context]);
  const [contextOpen, setContextOpen] = useState(false);
  const canPickFolder = typeof window !== 'undefined' && !!window.apolo?.repo?.pickFolder;
  // Auditoria 2026-07-02 (chat-hardening S2 #4): guard anti-double-submit — sem
  // ele, dois Enter em sequência (key-repeat/tap duplo) antes do re-render viam o
  // texto stale no closure e disparavam 2 createCodeSession = 2 sessões.
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const submitting = useRef(false);
  const taRef = useRef(null);
  const selectedMode = MODES.find((item) => item.id === mode) || MODES[0];
  const ModeIcon = selectedMode.icon;
  const creation = CREATION_INFO[mode] || null;
  const isCreation = !!creation;
  const model = useAgentStore((s) => s.code.ui.model);
  const setUiPref = useAgentStore((s) => s.setUiPref);
  const { groups } = useComposerModels();
  const files = useFileAttachments({ draftKey: draftScope });

  useEffect(() => { if (initialDraft?.model) setUiPref('model', initialDraft.model); }, [initialDraft, setUiPref]);

  useEffect(() => {
    writeDraft(typeof window !== 'undefined' ? window.localStorage : null, {
      text,
      environment,
      permissions_mode: permissionsMode,
      mode,
      model,
      scope: context?.kind || 'local',
      directory: context?.path || null,
      github_repo: context?.repo || null,
      client_intent_id: clientIntentId,
    }, draftScope);
  }, [text, mode, model, context, clientIntentId, draftScope, environment, permissionsMode]);

  const [savedDrafts, setSavedDrafts] = useState(() => listDrafts(window.localStorage, draftScope));
  const archiveCurrent = useCallback(async () => {
    if (!text.trim() && !files.attachments.length) return;
    if (files.attachments.length) await saveAttachmentDraft(`intent:${clientIntentId}`, files.attachments);
    const saved = writeDraft(window.localStorage, { text, mode, model, environment, permissions_mode: permissionsMode, directory: context?.path, github_repo: context?.repo, client_intent_id: clientIntentId }, `${draftScope}:archive:${clientIntentId}`);
    if (!saved) throw new Error('Não foi possível salvar o rascunho atual.');
    setSavedDrafts(listDrafts(window.localStorage, draftScope));
  }, [text, files.attachments, clientIntentId, mode, model, environment, permissionsMode, context, draftScope]);
  async function restoreSaved(id) {
    await archiveCurrent();
    const draft = readDraft(window.localStorage, `${draftScope}:archive:${id}`);
    if (!draft) return;
    const attachments = await loadAttachmentDraft(`intent:${id}`);
    setText(draft.text); setMode(draft.mode); setClientIntentId(id);
    setEnvironment(draft.environment || 'direct'); setPermissionsMode(draft.permissions_mode || 'ask');
    environmentChosen.current = true;
    setContext(draft.github_repo ? { kind: 'github', repo: draft.github_repo } : draft.directory ? { kind: 'folder', path: draft.directory } : null);
    if (draft.model) setUiPref('model', draft.model);
    files.restoreAttachments(attachments);
  }
  async function changeContext(next) {
    if (busy || !files.draftReady) return;
    try {
      await archiveCurrent();
      setClientIntentId(createClientIntentId());
      setContext(next);
      if (!environmentChosen.current) setEnvironment(projectEnvironment(window.localStorage, projectScope, next?.path));
    } catch (error) { files.setUploadError(error.message); }
  }

  const handledRequest = useRef(null);
  useEffect(() => {
    if (!newChatRequest || handledRequest.current === newChatRequest.id || !files.draftReady || busy || submitting.current) return;
    handledRequest.current = newChatRequest.id;
    submitting.current = true; setBusy(true);
    archiveCurrent().then(() => {
      setText(''); setClientIntentId(createClientIntentId()); setSubmitError(null);
      setContext(newChatRequest.github_repo ? {kind:'github',repo:newChatRequest.github_repo} : newChatRequest.directory ? { kind: 'folder', path: newChatRequest.directory } : null);
      environmentChosen.current = false;
      setEnvironment(projectEnvironment(window.localStorage, projectScope, newChatRequest.directory));
      files.clear();
      requestAnimationFrame(() => taRef.current?.focus());
    }).catch((error) => setSubmitError({ error: error.message })).finally(() => {
      submitting.current = false; setBusy(false);
      onNewChatHandled?.(newChatRequest.id);
    });
  }, [newChatRequest, files, busy, archiveCurrent, projectScope, onNewChatHandled]);

  // Autosize do textarea (até ~220px).
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 220)}px`;
  }, [text]);

  // "Nova sessão" (sidebar) limpa + foca o composer, mesmo já estando na home.
  useEffect(() => {
    async function onNew() {
      if (submitting.current || !files.draftReady) return;
      try { await archiveCurrent(); } catch (error) { files.setUploadError(error.message); return; }
      setText('');
      setClientIntentId(createClientIntentId());
      setMode('chat');
      setSubmitError(null);
      setContext(null);
      files.clear();
      setContextOpen(false);
      requestAnimationFrame(() => taRef.current?.focus());
    }
    window.addEventListener('apolo:chatNewSession', onNew);
    return () => window.removeEventListener('apolo:chatNewSession', onNew);
  });

  async function submit() {
    const t = text.trim();
    if (!t || busy || submitting.current || !files.draftReady) return;
    if (isCreation) {
      // Geração ainda não ligada (precisa de provider de imagem/vídeo + key nos
      // Adapters ⌘E). Avisa sem fingir output — o seletor de modelo já é o real.
      window.dispatchEvent(
        new CustomEvent('apolo:toast', {
          detail: {
            kind: 'info',
            message: `Modo ${creation.label}: geração chega em breve — adicione um provider (${creation.providers}) nos Adapters (⌘E) e eu ligo.`,
          },
        }),
      );
      return;
    }
    // busy até onSubmit resolver: num submit bem-sucedido o composer desmonta
    // (navega pra SessionWorkspace); numa falha, o finally reabilita.
    submitting.current = true;
    setBusy(true);
    setSubmitError(null);
    try {
      // Contexto escolhido (pasta local / repo GitHub) vira directory/github_repo
      // da sessão; sem contexto segue conversa pura.
      if (files.attachments.length) await saveAttachmentDraft(`intent:${clientIntentId}`, files.attachments);
      const result = await onSubmit?.({
        text: t, model, mode, environment_mode: context ? environment : 'direct', permissions_mode: permissionsMode, attachments: files.attachments,
        ...contextToSessionInput(context),
        client_intent_id: clientIntentId,
      });
      if (result?.ok === false) { setSubmitError(result); return; }
      setText('');
      clearDraft(typeof window !== 'undefined' ? window.localStorage : null, draftScope);
      setClientIntentId(createClientIntentId());
      files.clear();
    } catch (error) {
      setSubmitError({ error: error.message || 'Não foi possível enviar. Rascunho preservado.' });
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  // Abre o seletor de pasta nativo e fixa a pasta como contexto da sessão.
  async function pickFolder() {
    if (!canPickFolder) return;
    try {
      const r = await window.apolo.repo.pickFolder();
      const path = typeof r === 'string' ? r : r?.path;
      if (typeof path === 'string' && path.startsWith('/')) {
        await changeContext({ kind: 'folder', path, isGit: r?.isGit !== false });
      }
    } catch { /* cancelou / sem bridge */ }
  }

  // `@` (bare, no fim do texto) abre o seletor de contexto e remove o token —
  // espelha a disciplina end-anchored do atMenu do Code. O placeholder promete
  // "@ pra contexto"; aqui isso vira verdade.
  function onChangeText(e) {
    const v = e.target.value;
    const at = detectAtMenuPrefix(v);
    if (at && at.query === '' && v.endsWith('@')) {
      setText(v.slice(0, at.start) + v.slice(at.end));
      setContextOpen(true);
      return;
    }
    setText(v);
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  }

  // Insere o comando escolhido no lugar do /token (Home NÃO usa pills — entra
  // inline). `range` (caret-aware) troca EXATAMENTE o token e PRESERVA o texto
  // depois do cursor → invocar a skill no MEIO da frase funciona. Sem range →
  // fallback end-anchored. O popup consome Enter/Tab/setas em CAPTURE (sem submit).
  function handlePickCommand(cmd, range) {
    const { text: nextText, caret: nextCaret } = insertSlashToken(text, range, cmd.slash_command);
    setText(nextText);
    requestAnimationFrame(() => {
      const el = taRef.current;
      if (!el) return;
      el.focus();
      if (typeof nextCaret === 'number') el.setSelectionRange(nextCaret, nextCaret);
    });
  }

  return (
    <div className={unified ? 'workbench-composer' : 'w-full max-w-[640px]'} data-mode={mode} aria-busy={busy}>
      <div
        {...files.dropZoneHandlers}
        className="cx-composer relative px-3 pb-2 pt-3"
      >
        <input ref={files.fileInputRef} type="file" multiple hidden {...files.fileInputHandlers} />
        {/* `/` typeahead de skills (A2) — só no modo conversa; em imagem/vídeo o
            usuário descreve o prompt. Surface o /brain e demais comandos. */}
        {!isCreation && (
          <div className="absolute left-0 bottom-full z-30 mb-1">
            <SlashCommandAutocomplete
              input={text}
              onPick={handlePickCommand}
              anchorRef={taRef}
              onDismiss={() => taRef.current?.focus()}
            />
          </div>
        )}
        {files.dragOver && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] bg-apolo-surface1/85 text-sm text-apolo-textSecondary">
            Solte para anexar
          </div>
        )}

        {files.attachments.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {files.attachments.map((a) => (
              <span
                key={a.id}
                className="inline-flex max-w-[200px] items-center gap-1.5 rounded-lg border border-apolo-border bg-apolo-surface2 px-2 py-1 text-xs text-apolo-textSecondary"
              >
                {a.preview ? (
                  <img src={a.preview} alt="" className="h-4 w-4 rounded object-cover" />
                ) : (
                  <Paperclip size={12} aria-hidden="true" />
                )}
                <span className="truncate">{a.name}</span>
                <button
                  type="button"
                  onClick={() => files.removeAttachment(a.id)}
                  aria-label={`Remover ${a.name}`}
                  className="text-apolo-textTertiary transition hover:text-apolo-error"
                >
                  <X size={12} aria-hidden="true" />
                </button>
              </span>
            ))}
          </div>
        )}
        {files.uploadError && (
          <div className="mb-2 text-xs text-apolo-error">{files.uploadError}</div>
        )}

        <ComposerSubmitError error={submitError} busy={busy} onNewDraft={() => {
          setClientIntentId(createClientIntentId()); setSubmitError(null);
          taRef.current?.focus();
        }} />

        {savedDrafts.length > 0 && <select aria-label="Recuperar rascunho" className="min-h-11 rounded-lg bg-apolo-surface2 px-3 text-sm" value="" onChange={(e) => restoreSaved(e.target.value).catch((error) => files.setUploadError(error.message))}>
          <option value="">Recuperar rascunho…</option>
          {savedDrafts.map((draft) => <option key={draft.client_intent_id} value={draft.client_intent_id}>{draft.text.slice(0, 60) || 'Rascunho com anexos'}</option>)}
        </select>}
        {unified && mode === 'plan' && <div className="workbench-plan-hint"><ClipboardList size={16} /><span><strong>Planejamento</strong> Explore a ideia e revise o plano antes de executar.</span></div>}
        <textarea
          ref={taRef}
          value={text}
          onChange={onChangeText}
          onKeyDown={onKeyDown}
          rows={1}
          aria-label="Mensagem"
          placeholder={
            isCreation
              ? `Descreva ${creation.noun} que você quer gerar…`
              : mode === 'plan' ? 'O que você quer planejar? Descreva o objetivo e os detalhes…' : unified ? 'Peça ao Apolo para criar, investigar ou resolver algo…' : 'Planeje, construa, / pra skills, @ pra contexto…'
          }
          className="w-full resize-none bg-transparent text-sm leading-relaxed text-apolo-textPrimary placeholder:text-apolo-textPlaceholder focus:outline-none"
        />

        <div className={unified ? "workbench-composer-toolbar" : "mt-2 flex items-center gap-2"}>
          <button
            type="button"
            onClick={files.openFilePicker}
            aria-label="Anexar arquivo"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
          >
            <Paperclip size={16} aria-hidden="true" />
          </button>
          {/* Contexto: repo GitHub / pasta local / nenhum. Também abre pelo `@`. */}
          <ChatContextPicker
            value={context}
            onChange={changeContext}
            open={contextOpen}
            onOpenChange={setContextOpen}
            canPickFolder={canPickFolder}
            onPickFolder={pickFolder}
          />
          {unified && <WorkbenchPopover label="Modo de colaboração" className="workbench-control" trigger={<><ModeIcon size={15} /><span>{mode === 'plan' ? 'Planejamento' : selectedMode.label}</span><ChevronDown size={12} /></>}>
            {(close) => <><p className="workbench-menu-title">Como vamos trabalhar?</p>{MODES.filter((item) => item.ready).map((item) => { const Icon = item.icon; return <button key={item.id} className="workbench-menu-item" aria-pressed={mode === item.id} onClick={() => { setMode(item.id); close(); }}><Icon size={17} /><span><strong>{item.id === 'plan' ? 'Planejamento' : item.label}</strong><small>{{ chat: 'Conversar e construir', auto: 'Ajustar esforço, modelo e limites à tarefa', plan: 'Revisar o plano antes de executar', research: 'Investigar em profundidade', debug: 'Encontrar e corrigir problemas', ask: 'Explorar e entender, em leitura', image: 'Depende de provedor de imagens', video: 'Depende de provedor de vídeo' }[item.id]}</small></span></button>; })}</>}
          </WorkbenchPopover>}
          <ModelDropdown value={model} groups={groups} onChange={(id) => setUiPref('model', id)} triggerClassName={unified ? 'workbench-control workbench-model' : undefined} />
          <div className="workbench-composer-spacer flex-1" />
          {unified && <WorkbenchPopover label="Ambiente e permissões" className="workbench-control workbench-icon" trigger={<SlidersHorizontal size={17} />} width={310}>
            <div className="workbench-composer-settings"><p className="workbench-menu-title">Ambiente e permissões</p>
              {context && <label>Ambiente<select aria-label="Ambiente" value={environment} onChange={(e) => { environmentChosen.current = true; setEnvironment(e.target.value); saveProjectEnvironment(window.localStorage, projectScope, context?.path, e.target.value); }}><option value="direct">Pasta escolhida</option><option value="worktree">Worktree isolado</option></select></label>}
              <label>Permissões<select aria-label="Permissões" value={permissionsMode} onChange={(e) => setPermissionsMode(e.target.value)}><option value="ask">Perguntar antes de alterar</option><option value="ignore">Permitir alterações no contexto</option></select></label>
            </div>
          </WorkbenchPopover>}

          <button
            type="button"
            aria-label="Voz (em breve)"
            disabled
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-apolo-textTertiary opacity-50"
          >
            <Mic size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!text.trim() || busy || !files.draftReady}
            aria-label="Enviar"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-apolo-accent text-apolo-bg transition hover:brightness-110 disabled:opacity-40"
          >
            {busy ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <ArrowUp size={16} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {!unified && <div className="mt-3 flex flex-wrap justify-center gap-1.5">
        {MODES.map((m) => {
          const Icon = m.icon;
          const active = mode === m.id;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => m.ready && setMode(m.id)}
              disabled={!m.ready}
              title={m.ready ? m.label : `${m.label} — em breve`}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs transition ${
                active
                  ? 'border-apolo-accentBorder bg-apolo-accentMuted text-apolo-textPrimary'
                  : 'border-apolo-border text-apolo-textSecondary hover:bg-apolo-surface2 disabled:opacity-40 disabled:hover:bg-transparent'
              }`}
            >
              <Icon size={13} aria-hidden="true" className={active ? 'text-apolo-accent' : ''} />
              {m.label}
            </button>
          );
        })}
      </div>}

      {busy && <span role="status" className="sr-only">Criando chat…</span>}
      {unified && <div className="workbench-composer-caption"><span><ShieldQuestion size={12} />{mode === 'plan' ? 'Planejar antes de executar' : ['ask', 'research'].includes(mode) ? 'Somente leitura' : permissionsMode === 'ignore' ? 'Alterações permitidas no contexto' : 'Perguntar antes de alterar'}</span><span>Shift + Enter para nova linha</span></div>}
      {unified && <div className="workbench-starters" aria-label="Começar com uma ideia">
        {[
          { label: 'Planejar um projeto', description: 'Do objetivo aos próximos passos', mode: 'plan', icon: ClipboardList, text: 'Quero planejar um projeto. ' },
          { label: 'Investigar um problema', description: 'Encontre a causa e uma solução', mode: 'debug', icon: Bug, text: 'Me ajude a investigar este problema: ' },
          { label: 'Explorar uma ideia', description: 'Transforme uma ideia em possibilidades', mode: 'chat', icon: MessageCircle, text: 'Quero explorar esta ideia: ' },
        ].map((item) => { const Icon = item.icon; return <button key={item.mode} onClick={() => { setMode(item.mode); if (!text.trim()) setText(item.text); taRef.current?.focus(); }}>
          <Icon size={17} aria-hidden="true" />
          <span><strong>{item.label}</strong><small>{item.description}</small></span>
          <ArrowUpRight className="workbench-starter-arrow" size={14} aria-hidden="true" />
        </button>; })}
      </div>}
      {!unified && <div className="mt-4 flex flex-wrap justify-center gap-1.5">
        {QUICK.map((q) => (
          <button
            key={q.label}
            type="button"
            onClick={() => {
              setText(q.text);
              taRef.current?.focus();
            }}
            className="rounded-lg border border-apolo-border px-2.5 py-1 text-[11px] text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
          >
            {q.label}
          </button>
        ))}
      </div>}
    </div>
  );
}
